import React, { type ChangeEvent, useRef } from 'react';
import uploadLogo from '../../assets/upload.svg';
import video from '../../assets/video.png';
import trashIcon from '../../assets/trash.svg';
import PublicationImageWithLogo from '../PublicationImageWithLogo';

const trash = { path: trashIcon };

type CategoryConfigUploadFileProps = {
    media: string;
    extension: string;
    acceptedFormats: string[];
    acceptedFormatsHelper: string[];
    handleMediaChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleResetMedia: () => void;
};

type PublicationUploadFileProps = {
    media: string;
    extension: string;
    handleMediaChange: (e: ChangeEvent<HTMLInputElement>, networkKey?: string) => void;
    resetMedia: (networkKey?: string) => void;
    disabled: boolean;
    networkKey?: string;
    isGlobal?: boolean;
    isViewMode?: boolean;
};

export type UploadFileProps = CategoryConfigUploadFileProps | PublicationUploadFileProps;

function isCategoryConfigUpload(props: UploadFileProps): props is CategoryConfigUploadFileProps {
    return 'acceptedFormats' in props;
}

export function UploadFile(props: UploadFileProps) {
    const inputFile = useRef<HTMLInputElement>(null);

    if (isCategoryConfigUpload(props)) {
        const {
            media,
            extension,
            acceptedFormats,
            acceptedFormatsHelper,
            handleMediaChange,
            handleResetMedia,
        } = props;

        return (
            <div className="flex flex-col items-center content-center justify-evenly w-full h-[300px] bg-primary-light border border-dashed border-dark-gray rounded-[5px] overflow-hidden relative font-montserrat">
                {!media && (
                    <>
                        <img src={uploadLogo} width="32" height="32" alt="upload-logo" />
                        <span className="relative z-10 text-medium font-medium">
                            Déposer vos fichiers ici
                        </span>
                        <span className="relative z-10 text-medium">ou</span>
                        <span className="flex rounded-[24px] p-[12px] bg-white border border-light-gray text-medium font-small cursor-pointer">
                            Ajouter un fichier
                        </span>
                        <span className="flex text-small">
                            {acceptedFormatsHelper.join(', ')} acceptés.
                        </span>
                    </>
                )}
                <input
                    name="media"
                    type="file"
                    className="flex opacity-0 absolute w-full h-full top-0 left-0 z-10 cursor-pointer"
                    accept={acceptedFormats.join(', ')}
                    onChange={handleMediaChange}
                    onClick={e => e.stopPropagation()}
                    multiple={false}
                />
                {media && (
                    <div className="absolute inset-0 w-full h-full bg-light-gray z-[100]">
                        <button
                            className="absolute top-[12px] right-[12px] w-[32px] h-[32px] bg-white rounded-[4px] m-0 p-0 z-[101] flex items-center justify-center"
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleResetMedia();
                            }}
                        >
                            <img
                                src={trash.path}
                                alt="trash icon"
                                className="w-4 h-4"
                                aria-hidden="true"
                            />
                        </button>
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <PublicationImageWithLogo
                                imageUrl={media}
                                alt={media}
                                className=""
                                isGlobal={true}
                                showLogo={true}
                                extension={extension}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    const {
        handleMediaChange,
        extension,
        resetMedia,
        disabled,
        media,
        networkKey,
        isGlobal = false,
        isViewMode = false,
    } = props;

    if (isViewMode && !media) {
        return null;
    }

    return (
        <>
            <div className="flex flex-col items-center content-center justify-evenly w-full h-[323px] bg-primary-light border border-dashed border-dark-gray rounded-[5px] overflow-hidden relative font-montserrat">
                {!media && (
                    <>
                        <img src={uploadLogo} width="32" height="32" alt="upload-logo" />
                        <span className="relative z-10 text-medium font-medium">
                            Déposer vos fichiers ici
                        </span>
                        <span className="relative z-10 text-medium">ou</span>
                        <span className="flex rounded-[24px] p-[12px] bg-white border border-light-gray text-medium font-small cursor-pointer">
                            Ajouter un fichier
                        </span>
                        <span className="flex text-small">
                            {networkKey !== 'googleMyBusiness'
                                ? '10Mo max, .jpg, .mp4 acceptés'
                                : '10Mo max, .jpg acceptés'}
                        </span>
                    </>
                )}
                {!isViewMode && (
                    <input
                        ref={inputFile}
                        name="media"
                        id={networkKey || 'basePublication'}
                        type="file"
                        className="flex opacity-0 absolute w-full h-full top-0 left-0 z-10 cursor-pointer"
                        accept={
                            networkKey !== 'googleMyBusiness'
                                ? 'image/jpeg, image/jpg, video/mp4'
                                : 'image/jpeg, image/jpg'
                        }
                        onChange={e => handleMediaChange(e, networkKey)}
                        onClick={e => e.stopPropagation()}
                        multiple={false}
                        disabled={disabled}
                    />
                )}
                {media && (
                    <div className="absolute inset-0 w-full h-full bg-light-gray z-[100]">
                        {!isViewMode && (
                            <button
                                className="absolute top-[12px] right-[12px] w-[32px] h-[32px] bg-white rounded-[4px] m-0 p-0 z-[101] flex items-center justify-center"
                                onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    resetMedia(networkKey);
                                }}
                            >
                                <img
                                    src={trash.path}
                                    alt="trash icon"
                                    className="w-4 h-4"
                                    aria-hidden="true"
                                />
                            </button>
                        )}
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <PublicationImageWithLogo
                                imageUrl={extension === 'mp4' ? video : media}
                                alt={media}
                                className=""
                                isGlobal={isGlobal}
                                showLogo={true}
                                extension={extension}
                            />
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
